import { SOCKET_URL } from 'settings';
import { getValidAccessToken } from 'services/Api/refreshHelper';
import { getLogoutMarker, rejectBrowserLogin } from 'services/logoutSync';
import { PRESENCE_CLOSED } from 'services/loginProtocol';
import { sessionPresenceChanged } from 'services/sessionPresence';
import { bootstrapMetadata } from 'services/monitoring';

type Change = {
  path: Array<string | number>;
  value?: unknown;
  delete?: boolean;
};
/** Copy only changed branches so open forms and unchanged charts retain their identity. */
export function applyChanges<T>(source: T, changes: Change[]): T {
  const apply = (value: any, path: Change['path'], change: Change): any => {
    if (!path.length) return change.value;
    const [key, ...tail] = path;
    if (['__proto__', 'prototype', 'constructor'].includes(String(key)))
      throw new Error('Invalid update path');
    const result = Array.isArray(value) ? [...value] : { ...value };
    if (!tail.length && change.delete) delete result[key];
    else result[key] = apply(value?.[key], tail, change);
    return result;
  };
  return changes.reduce(
    (value, change) => apply(value, change.path, change),
    source,
  );
}

/** Event-driven transport: timers reconnect failed sockets; they never fetch session data. */
export function openNativeSocket<T>(
  sessionId: number,
  surface: string,
  receive: (data: T) => void,
  status: (connected: boolean, error?: string) => void,
) {
  let socket: WebSocket | null = null;
  let stopped = false,
    attempt = 0,
    sequence = 0;
  let value: T | null = null;
  let retry: ReturnType<typeof setTimeout> | undefined;
  let selection: Record<string, unknown> = {};
  let connections = 0;
  const subscribe = (ws: WebSocket, trigger?: 'reconnect' | 'resync') => {
    const monitoring = bootstrapMetadata(sessionId, trigger);
    ws.send(JSON.stringify({ type: 'subscribe', surface, ...(monitoring ? { monitoring } : {}) }));
  };
  const marker = getLogoutMarker();
  const current = () => !stopped && getLogoutMarker() === marker;
  const connect = async () => {
    try {
      const token = await getValidAccessToken();
      if (!current()) return;
      if (!token) {
        status(false, 'Please sign in again.');
        return;
      }
      const ws = new WebSocket(
        `${SOCKET_URL}/fast/sessions/${sessionId}/?token=${encodeURIComponent(token)}&presence=1`,
      );
      socket = ws;
      ws.onopen = () => {
        if (current()) subscribe(ws, connections++ ? 'reconnect' : undefined);
      };
      ws.onmessage = event => {
        if (!current() || ws !== socket) return;
        try {
          const message = JSON.parse(event.data);
          if (message.type === 'presence_changed') {
            sessionPresenceChanged(message.data?.session_id);
            return;
          }
          if (message.type === 'error') {
            status(true, message.detail);
            return;
          }
          if (message.type === 'snapshot') {
            value = message.data;
            sequence = message.sequence;
            if (Object.keys(selection).length)
              ws.send(JSON.stringify({ type: 'select', ...selection }));
          } else if (
            message.type === 'patch' &&
            value &&
            message.base === sequence
          ) {
            value = applyChanges(value, message.changes);
            sequence = message.sequence;
          } else {
            // A missed connection-local sequence resynchronizes once, over this socket.
            subscribe(ws, 'resync');
            return;
          }
          attempt = 0;
          status(true);
          receive(value as T);
        } catch (reason) {
          status(false, String(reason));
          ws.close();
        }
      };
      ws.onclose = event => {
        if (!current() || ws !== socket) return;
        if (event.code === PRESENCE_CLOSED) {
          stopped = true;
          rejectBrowserLogin(token);
          status(false, 'Please sign in again.');
          return;
        }
        status(
          false,
          event.code === 4403
            ? 'Session access is no longer available.'
            : undefined,
        );
        if (event.code === 4403) {
          stopped = true;
          return;
        }
        retry = setTimeout(
          connect,
          Math.min(10000, 500 * 2 ** Math.min(attempt++, 5)),
        );
      };
      ws.onerror = () => ws.close();
    } catch (reason) {
      if (!current()) return;
      status(false, String(reason));
      retry = setTimeout(
        connect,
        Math.min(10000, 500 * 2 ** Math.min(attempt++, 5)),
      );
    }
  };
  void connect();
  return {
    select: (next: Record<string, unknown>) => {
      selection = { ...selection, ...next };
      if (current() && socket?.readyState === WebSocket.OPEN && value)
        socket.send(JSON.stringify({ type: 'select', ...next }));
    },
    close: () => {
      stopped = true;
      clearTimeout(retry);
      socket?.close();
    },
  };
}
